import { Aide } from '@/components/ui/aide';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Calendar,
  Camera,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Fence,
  Gauge,
  MapPin,
  RefreshCw,
  ShieldAlert,
  Users,
} from 'lucide-react';

import { cn } from '../lib/utils';
import { messageErreur } from '../lib/erreurs';
import { formatDateHeure } from '../lib/formats';
import { libelle, texteSur } from '../lib/libelles';
import { LEXIQUE } from '../lib/lexique';
import { fieldOpsAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { ONBOARDING_ROUTE } from '../components/onboarding';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';
import { EmptyState } from '../components/ui/empty-state';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePaginationServeur } from '../hooks/usePagination';
import { useDerniereDemande } from '../hooks/useDerniereDemande';
import { PageHero } from '../components/dashboard/PageHero';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

/**
 * Terrain — ce que l'application chauffeur enregistre sur la route, relu par
 * le bureau.
 *
 * Le terminal écrit depuis longtemps les attentes aux frontières, les
 * checklists de départ, les incidents et les relevés de jauge, et rien ne les
 * affichait. L'écran est construit autour de quatre questions plutôt qu'autour
 * des quatre tables :
 *
 *   Où perd-on du temps ?            attente cumulée et médiane, poste par poste
 *   Pourquoi ?                       le motif de blocage derrière chaque arrêt
 *   Qu'est-ce qui est ouvert ?       les incidents non résolus, par gravité
 *   Les départs sont-ils conformes ? les points critiques réellement cochés
 *
 * La couleur marque un état et jamais un emplacement : un nombre d'arrêts
 * n'est pas une alarme, un incident critique non résolu en est une. Ce qui
 * bloque prend une ligne entière avec son motif ; ce qui va bien se replie en
 * pastille ou en une seule phrase de réassurance.
 */

// L'onglet actif vit dans l'adresse (`?tab=`): le retour arrière revient à
// l'onglet précédent et un lien partagé ouvre le bon. Les onglets pointaient
// avant vers `/field-ops/incidents`, `/field-ops/departures`… qu'aucune route ne
// connaît: la route générale renvoyait alors à l'accueil, et la page se
// rechargeait entièrement.
const NAV_ITEMS = [
  // L'icône n'est pas un ornement : c'est elle qu'on retrouve d'un coup d'œil
  // quand on revient sur l'écran, le libellé ne se lit qu'ensuite.
  { tab: 'frontieres', key: 'border', label: 'Frontières', icone: Fence },
  { tab: 'incidents', key: 'incidents', label: 'Incidents', icone: AlertTriangle },
  { tab: 'departs', key: 'departures', label: 'Départs', icone: ArrowUpRight },
  { tab: 'jauges', key: 'gauges', label: 'Jauges', icone: BarChart3 },
];

const PAGE_CONFIG = {
  border: {
    // `fil` remplace le sourcil du bandeau : le fil d'Ariane dit déjà « Terrain
    // › Corridor » au-dessus du titre, et le répéter en capitales juste en
    // dessous ne ferait que voler deux lignes au titre.
    fil: 'Corridor',
    title: 'Attente aux postes frontière',
    description:
      'Le temps passé à l’arrêt à chaque poste frontière, et la raison de l’arrêt. C’est la première cause de retard sur un trajet international.',
  },
  incidents: {
    fil: 'Sécurité',
    title: 'Incidents signalés',
    description:
      'Urgences remontées depuis le camion : accident, panne, agression, fuite produit. Ce qui est encore ouvert passe en premier.',
  },
  departures: {
    fil: 'Conformité',
    title: 'Contrôles avant départ',
    description:
      'Les huit points critiques cochés, ou non, avant qu’un camion prenne la route. C’est la pièce que réclame un client pétrolier.',
  },
  gauges: {
    fil: 'Volumes',
    title: 'Relevés de jauge',
    description:
      'Volumes relevés au chargement et à la livraison, et la photo qui les justifie. L’écart entre le chargé et le livré se contrôle dans « Livraisons à contrôler ».',
  },
};

const PERIODS = [
  { value: '7', label: '7 jours' },
  { value: '30', label: '30 jours' },
  { value: '90', label: '90 jours' },
];

/** Les libellés des motifs, pièces, points de contrôle, types d'incident et gravités viennent de `lib/libelles`. */
const RELAY_LABELS = {
  relayed: 'Transmis à l’entretien',
  skipped_module_off: 'Entretien non activé',
  skipped_no_truck: 'Aucun camion connu',
  relay_failed: 'Transmission à l’entretien échouée',
};


const num = (value) => Number(value || 0);
const hours = (seconds) => Math.round((num(seconds) / 3600) * 10) / 10;
const minutes = (seconds) => Math.round(num(seconds) / 60);

/**
 * Le tiret n'est pas une coquetterie typographique.
 *
 * « 0 h d'attente » est une mesure : elle affirme que des camions sont passés
 * et n'ont pas attendu. Tant qu'aucun arrêt n'a été chronométré, c'est faux —
 * on ne sait pas. « — h » le dit, et évite qu'un exploitant conclue que le
 * corridor est fluide alors que le terminal ne remonte simplement rien.
 */
const mesure = (valeur, unite) => {
  if (valeur === null || valeur === undefined || valeur === '') return `— ${unite}`;
  // « 12,5 h » et non « 12.5 h » : l'écran se lit en français, séparateur
  // décimal compris.
  const ecrit =
    typeof valeur === 'number'
      ? valeur.toLocaleString('fr-FR', { maximumFractionDigits: 1 })
      : valeur;
  return `${ecrit} ${unite}`;
};

/** "3 h 20" / "45 min" — la façon dont un répartiteur le dit à voix haute. */
const duration = (seconds) => {
  if (seconds === null || seconds === undefined || seconds === '') return '—';
  const total = Math.max(0, Math.round(num(seconds) / 60));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`;
};

/** « Il y a 4 min » : la fraîcheur de ce qui est à l'écran, pas une date. */
const fraicheur = (instant, maintenant) => {
  if (!instant) return 'Jamais chargé';
  const ecoulees = Math.max(0, Math.floor((maintenant - instant) / 60000));
  if (ecoulees < 1) return 'À l’instant';
  if (ecoulees < 60) return `Il y a ${ecoulees} min`;
  return `Il y a ${Math.floor(ecoulees / 60)} h`;
};

const dateTime = (value) => (value ? formatDateHeure(value) : 'Date inconnue');
const reasonLabel = (code) => libelle('motifBlocage', code, 'Motif non précisé');
/** Un texte venu du terminal (plaque, trajet, chauffeur): jamais un identifiant. */
const lisible = (valeur) => texteSur(valeur, '');
const liters = (value) =>
  `${num(value).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} L`;

const emptyState = {
  overview: null,
  border: null,
  incidents: null,
  departures: null,
  departuresFailed: null,
  gauges: null,
  gaugesNoPhoto: null,
};

/**
 * Le pied de la grande carte vide — état des systèmes, fraîcheur de la donnée,
 * bouton de rafraîchissement — appartient à la page, mais s'affiche à
 * l'intérieur de la carte que rendent les quatre vues. Le faire descendre en
 * prop à travers `BorderView`, `IncidentsView`, `DeparturesView` et
 * `GaugesView` laisserait croire que c'est une donnée de la vue ; un contexte
 * local au fichier dit ce que c'est : un décor partagé.
 */
const ContexteFraicheur = React.createContext(null);

export default function FieldOpsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeItem =
    NAV_ITEMS.find((item) => item.tab === searchParams.get('tab')) || NAV_ITEMS[0];
  const config = PAGE_CONFIG[activeItem.key];

  // Un nouvel enregistrement d'historique par onglet (pas de `replace`) : c'est
  // ce qui fait que « retour » ramène à l'onglet d'avant.
  const selectTab = (tab) => {
    if (tab === searchParams.get('tab')) return;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('tab', tab);
      return next;
    });
  };

  const [days, setDays] = useState('30');
  const [data, setData] = useState(emptyState);
  const [loading, setLoading] = useState(true);
  // Distingué de « aucune donnée » : une requête en échec a déjà produit un
  // écran vide identique qui affirmait que le corridor était dégagé.
  const [loadFailed, setLoadFailed] = useState(false);
  const [openOnly, setOpenOnly] = useState(true);
  const [derniereMaj, setDerniereMaj] = useState(null);
  const [maintenant, setMaintenant] = useState(() => Date.now());

  // Chaque liste se lit page par page (le serveur donne le total, la page vit
  // dans l'adresse): rien n'est coupé en silence, même à 400 lignes.
  const pagArrets = usePaginationServeur(data.border?.total ?? 0, { cle: 'arrets', reinitialiser: days });
  const pagIncidents = usePaginationServeur(data.incidents?.total ?? 0, {
    cle: 'incidents',
    reinitialiser: `${days}|${openOnly}`,
  });
  const pagControles = usePaginationServeur(data.departures?.total ?? 0, { cle: 'controles', reinitialiser: days });
  const pagNonConformes = usePaginationServeur(data.departuresFailed?.total ?? 0, {
    cle: 'non-conformes',
    reinitialiser: days,
  });
  const pagReleves = usePaginationServeur(data.gauges?.total ?? 0, { cle: 'releves', reinitialiser: days });
  const pagSansPhoto = usePaginationServeur(data.gaugesNoPhoto?.total ?? 0, { cle: 'sans-photo', reinitialiser: days });

  const surIncidents = activeItem.key === 'incidents';
  const surDeparts = activeItem.key === 'departures';
  const surJauges = activeItem.key === 'gauges';

  const signaler = useCallback((error) => {
    if (error?.response?.status === 403) {
      toast.error('Le suivi sur la route n’est pas inclus dans votre abonnement.');
    } else {
      toast.error(messageErreur(error, 'Impossible de charger les données du terrain.'));
    }
  }, []);

  // Un chargement en cours, réussi ou en échec: le bandeau « Données non chargées » en dépend.
  const suivre = useCallback(async (travail) => {
    setLoading(true);
    try {
      await travail();
      setLoadFailed(false);
      setDerniereMaj(Date.now());
      setMaintenant(Date.now());
    } catch (error) {
      setLoadFailed(true);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const garder = useCallback((cle, valeur) => setData((courant) => ({ ...courant, [cle]: valeur })), []);

  const chargerApercu = useCallback(
    async () => garder('overview', await fieldOpsAPI.getOverview({ days: Number(days) })),
    [days, garder],
  );
  // Les camions à la douane du bandeau viennent de cette lecture: elle se fait sur tous les onglets.
  // Chaque liste a sa propre dernière demande: une réponse plus ancienne n'écrase pas la page affichée.
  const demandeFrontieres = useDerniereDemande();
  const demandeIncidents = useDerniereDemande();
  const demandeControles = useDerniereDemande();
  const demandeReleves = useDerniereDemande();
  const chargerFrontieres = useCallback(async () => {
    const perimee = demandeFrontieres();
    const arrets = await fieldOpsAPI.getBorderEvents({ days: Number(days), page: pagArrets.page, page_size: pagArrets.taille });
    if (perimee()) return;
    garder('border', arrets);
  }, [days, garder, pagArrets.page, pagArrets.taille, demandeFrontieres]);
  const chargerIncidents = useCallback(async () => {
    const perimee = demandeIncidents();
    const incidents = await fieldOpsAPI.getIncidents({
      days: Number(days),
      ...(openOnly ? { status: 'open' } : {}),
      page: pagIncidents.page,
      page_size: pagIncidents.taille,
    });
    if (perimee()) return;
    garder('incidents', incidents);
  }, [days, openOnly, garder, pagIncidents.page, pagIncidents.taille, demandeIncidents]);
  const chargerControles = useCallback(async () => {
    const perimee = demandeControles();
    const [historique, nonConformes] = await Promise.all([
      fieldOpsAPI.getDepartureChecklists({ days: Number(days), page: pagControles.page, page_size: pagControles.taille }),
      fieldOpsAPI.getDepartureChecklists({
        days: Number(days),
        conformity: 'incomplete',
        page: pagNonConformes.page,
        page_size: pagNonConformes.taille,
      }),
    ]);
    if (perimee()) return;
    garder('departures', historique);
    garder('departuresFailed', nonConformes);
  }, [days, garder, pagControles.page, pagControles.taille, pagNonConformes.page, pagNonConformes.taille, demandeControles]);
  const chargerReleves = useCallback(async () => {
    const perimee = demandeReleves();
    const [releves, sansPhoto] = await Promise.all([
      fieldOpsAPI.getGaugeReadings({ days: Number(days), page: pagReleves.page, page_size: pagReleves.taille }),
      fieldOpsAPI.getGaugeReadings({
        days: Number(days),
        photo: 'without',
        page: pagSansPhoto.page,
        page_size: pagSansPhoto.taille,
      }),
    ]);
    if (perimee()) return;
    garder('gauges', releves);
    garder('gaugesNoPhoto', sansPhoto);
  }, [days, garder, pagReleves.page, pagReleves.taille, pagSansPhoto.page, pagSansPhoto.taille, demandeReleves]);

  // Actualiser relit tout ce qui est à l'écran.
  const load = useCallback(
    () =>
      suivre(() =>
        Promise.all([
          chargerApercu(),
          chargerFrontieres(),
          ...(surIncidents ? [chargerIncidents()] : []),
          ...(surDeparts ? [chargerControles()] : []),
          ...(surJauges ? [chargerReleves()] : []),
        ]),
      ),
    [suivre, chargerApercu, chargerFrontieres, chargerIncidents, chargerControles, chargerReleves, surIncidents, surDeparts, surJauges],
  );

  // Une page ne relit que sa propre liste: tourner les pages des incidents ne recalcule pas les indicateurs.
  useEffect(() => {
    suivre(chargerApercu).catch(signaler);
  }, [suivre, chargerApercu, signaler]);
  useEffect(() => {
    suivre(chargerFrontieres).catch(signaler);
  }, [suivre, chargerFrontieres, signaler]);
  useEffect(() => {
    if (surIncidents) suivre(chargerIncidents).catch(signaler);
  }, [surIncidents, suivre, chargerIncidents, signaler]);
  useEffect(() => {
    if (surDeparts) suivre(chargerControles).catch(signaler);
  }, [surDeparts, suivre, chargerControles, signaler]);
  useEffect(() => {
    if (surJauges) suivre(chargerReleves).catch(signaler);
  }, [surJauges, suivre, chargerReleves, signaler]);

  useEffect(() => {
    // Sans horloge, le pied de carte afficherait « À l'instant » un quart
    // d'heure durant : une fraîcheur figée ment davantage que pas de fraîcheur.
    const battement = setInterval(() => setMaintenant(Date.now()), 30000);
    return () => clearInterval(battement);
  }, []);

  const overview = data.overview || {};
  const borderStats = overview.border || {};
  const checkStats = overview.checklists || {};
  const incidentStats = overview.incidents || {};
  const gaugeStats = overview.gauges || {};

  const posts = borderStats.posts || [];
  const worstPost = posts[0];
  // Les camions dont le trajet est, en ce moment, à une douane.
  const atCustoms = useMemo(() => data.border?.at_customs || [], [data.border]);

  // Les incidents de la page, le nombre d'ouverts et le total viennent du serveur: l'aperçu les compte sur toute la période.
  const incidentItems = useMemo(() => data.incidents?.items || [], [data.incidents]);

  const checklistItems = useMemo(() => data.departures?.items || [], [data.departures]);
  const failedChecklists = useMemo(() => data.departuresFailed?.items || [], [data.departuresFailed]);

  const tauxConformite =
    checkStats.compliance_rate === null || checkStats.compliance_rate === undefined
      ? null
      : checkStats.compliance_rate;

  // Rien de mesuré n'est pas un mauvais résultat : c'est l'absence de résultat,
  // donc un ton neutre. Une fois mesuré, le taux devient un état et prend sa
  // couleur.
  const tonConformite = (() => {
    if (tauxConformite === null) return 'neutre';
    if (tauxConformite >= 100) return 'disponible';
    if (tauxConformite >= 80) return 'atelier';
    return 'critique';
  })();

  // Zéro incident ouvert est un état vérifié — vert. Un seul incident encore
  // ouvert demande une décision aujourd'hui : il n'y a pas d'échelon tiède
  // entre les deux.
  const incidentsOuverts = num(incidentStats.open);

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      {/* Discret et au-dessus du titre : il situe l'écran dans le module sans
          prendre la place d'un sourcil en capitales. */}
      <Breadcrumb>
        <BreadcrumbList className="text-xs">
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <button
                type="button"
                onClick={() => selectTab(NAV_ITEMS[0].tab)}
                className="cursor-pointer"
              >
                {LEXIQUE.menuDouane}
              </button>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{config.fil}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <PageHero
        accroche="Des corridors plus fluides pour l’Afrique"
        illustration="transport"
        illustrationPlace="fond"
        statsDisposition="ligne"
        eyebrow={LEXIQUE.menuDouane}
        title={config.title}
        description={config.description}
        stats={[
          {
            // Le nombre en clair: un camion bloqué à la douane se voit d'un
            // coup d'œil, même avant le premier passage mesuré.
            label: 'À la douane',
            valeur: atCustoms.length,
            ton: atCustoms.length ? 'attention' : 'neutre',
            aide: atCustoms.length
              ? `${atCustoms.length} camion(s) à la douane en ce moment`
              : 'Aucun camion à la douane en ce moment',
          },
          {
            label: 'Attente frontière',
            valeur: mesure(
              borderStats.timed_stops ? hours(borderStats.total_wait_seconds) : null,
              'h',
            ),
            ton: 'neutre',
            aide: borderStats.timed_stops
              ? `${borderStats.timed_stops} arrêt(s) mesuré(s)`
              : atCustoms.length
                ? `${atCustoms.length} camion(s) à la douane`
                : 'Aucun arrêt mesuré',
          },
          {
            label: 'Attente médiane',
            valeur: mesure(
              borderStats.median_wait_seconds === null ||
                borderStats.median_wait_seconds === undefined
                ? null
                : minutes(borderStats.median_wait_seconds),
              'min',
            ),
            aide: worstPost
              ? `Poste le plus lent : ${texteSur(worstPost.location_name, 'poste non identifié')}`
              : 'Par arrêt',
          },
          {
            label: 'Incidents ouverts',
            valeur: incidentsOuverts,
            ton: incidentsOuverts ? 'critique' : 'positif',
            aide: num(incidentStats.open_critical)
              ? `dont ${incidentStats.open_critical} critique(s)`
              : 'Aucun incident critique',
          },
          {
            label: 'Départs conformes',
            valeur: mesure(tauxConformite, '%'),
            ton:
              tonConformite === 'disponible'
                ? 'positif'
                : tonConformite === 'atelier'
                  ? 'attention'
                  : tonConformite === 'critique'
                    ? 'critique'
                    : 'neutre',
            aide: checkStats.total
              ? `${checkStats.complete}/${checkStats.total} départs contrôlés`
              : 'Aucun contrôle enregistré',
          },
        ]}
        alertes={[
          loadFailed && {
            niveau: 'critique',
            court: 'Données non chargées',
            texte:
              'Les données du terrain n’ont pas pu être chargées. Rien ci-dessous ne reflète la situation réelle.',
            action: { label: 'Réessayer', onClick: load },
          },
        ]}
      />

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <nav
          className="flex flex-wrap items-center gap-1 rounded-2xl border border-border/70 bg-card p-1 shadow-sm"
          aria-label="Rubriques de la page"
        >
          {NAV_ITEMS.map((item) => {
            const actif = item.key === activeItem.key;
            const Icone = item.icone;
            return (
              <button
                key={item.tab}
                type="button"
                onClick={() => selectTab(item.tab)}
                aria-current={actif ? 'page' : undefined}
                className={cn(
                  'inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  actif
                    ? // Pétrole plein : l'onglet actif est le seul élément
                      // saturé de la barre, on le trouve sans le chercher.
                      'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                <Icone className="h-4 w-4" aria-hidden="true" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger
              className="w-36 justify-start gap-2 [&>svg:last-child]:ml-auto"
              aria-label="Période observée"
            >
              <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((period) => (
                <SelectItem key={period.value} value={period.value}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw
              className={cn('h-4 w-4', loading && 'animate-spin')}
              aria-hidden="true"
            />
            Actualiser
          </Button>
        </div>
      </div>

      <ContexteFraicheur.Provider
        value={{
          derniereMaj,
          maintenant,
          loading,
          enEchec: loadFailed,
          onRafraichir: () => {
            load().catch((error) => toast.error(messageErreur(error, 'Impossible de charger les données du terrain.')));
          },
        }}
      >
        {activeItem.key === 'border' && (
          <BorderView
            aggregate={data.border?.aggregate || borderStats}
            items={data.border?.items || []}
            pagination={pagArrets}
            atCustoms={atCustoms}
            maintenant={maintenant}
          />
        )}

        {activeItem.key === 'incidents' && (
          <IncidentsView
            items={incidentItems}
            openCount={num(incidentStats.open)}
            totalCount={num(incidentStats.total)}
            pagination={pagIncidents}
            openOnly={openOnly}
            onToggle={() => setOpenOnly((value) => !value)}
          />
        )}

        {activeItem.key === 'departures' && (
          <DeparturesView
            items={checklistItems}
            total={data.departures?.total ?? checklistItems.length}
            pagination={pagControles}
            failed={failedChecklists}
            failedTotal={data.departuresFailed?.total ?? failedChecklists.length}
            paginationFailed={pagNonConformes}
            aggregate={data.departures?.aggregate || checkStats}
          />
        )}

        {activeItem.key === 'gauges' && (
          <GaugesView
            items={data.gauges?.items || []}
            total={data.gauges?.total ?? (data.gauges?.items || []).length}
            pagination={pagReleves}
            withoutPhoto={data.gaugesNoPhoto?.items || []}
            withoutPhotoTotal={data.gaugesNoPhoto?.total ?? 0}
            paginationWithoutPhoto={pagSansPhoto}
            stats={gaugeStats}
          />
        )}
      </ContexteFraicheur.Provider>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Frontières                                                          */
/* ------------------------------------------------------------------ */

function BorderView({ aggregate, items, pagination, atCustoms = [], maintenant }) {
  const posts = aggregate.posts || [];
  const reasons = aggregate.reasons || [];
  const pieces = aggregate.missing_pieces || [];

  if (!posts.length && !atCustoms.length) {
    return (
      <CarteVide
        illustration="zone"
        icon={Clock}
        title="Aucun passage de poste frontière sur la période"
        lines={[
          'L’application chauffeur ouvre un chronomètre dès qu’un camion entre dans la zone d’un poste, et le ferme à la sortie.',
          'Un camion apparaît aussi ici dès que son trajet passe à « À la douane de sortie » ou « À la douane d’entrée ».',
          'Si des camions passent la frontière sans rien remonter ici : vérifiez que les postes sont créés dans les zones géographiques et que les chauffeurs utilisent la dernière version de l’application.',
        ]}
        conseil="Les temps d’attente viennent de l’application chauffeur (entrée et sortie de chaque poste) et des étapes de douane des trajets."
      />
    );
  }

  return (
    <div className="space-y-4">
      {atCustoms.length > 0 && (
        <Panel
          title="Camions à la douane en ce moment"
          description="D’après l’étape de leur trajet. L’attente d’un camion compte dans les temps ci-dessous quand il repart de la douane."
        >
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            {atCustoms.map((camion) => (
              <CamionEnDouane key={camion.trip_id} camion={camion} maintenant={maintenant} />
            ))}
          </div>
        </Panel>
      )}

      {posts.length > 0 && (
        <PostesView posts={posts} reasons={reasons} pieces={pieces} recent={items} pagination={pagination} />
      )}
    </div>
  );
}

/** Les temps d'attente par poste: où ils se perdent, pourquoi, et le détail des derniers arrêts. */
function PostesView({ posts, reasons, pieces, recent, pagination }) {
  return (
    <>
      <Panel
        title="Où le temps se perd"
        description="Postes classés par temps immobilisé cumulé. Un poste qui a bloqué au moins un camion porte son motif."
      >
        {posts.map((post) => (
          <PostRow key={post.key} post={post} />
        ))}
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel
          title="Motifs de blocage"
          description={
            reasons.length
              ? 'Ce qui a réellement retenu les camions.'
              : undefined
          }
        >
          {reasons.length ? (
            <div className="flex flex-wrap gap-2">
              {reasons.map((reason) => (
                <Badge
                  key={reason.reason}
                  className="bg-muted text-muted-foreground"
                >
                  {reasonLabel(reason.reason)} · {reason.stops} ·{' '}
                  {duration(reason.total_seconds)}
                </Badge>
              ))}
            </div>
          ) : (
            <Reassurance text="Aucun arrêt déclaré comme blocage : le temps mesuré est du passage normal." />
          )}
        </Panel>

        <Panel
          title="Pièces manquantes"
          description={
            pieces.length
              ? 'Documents réclamés au poste et absents du dossier.'
              : undefined
          }
        >
          {pieces.length ? (
            <div className="flex flex-wrap gap-2">
              {pieces.map((piece) => (
                <Badge
                  key={piece.piece}
                  className="bg-destructive/15 text-destructive"
                >
                  {libelle('pieceExigee', piece.piece, 'Autre pièce')} · {piece.count}
                </Badge>
              ))}
            </div>
          ) : (
            <Reassurance text="Aucune pièce signalée manquante à un poste." />
          )}
        </Panel>
      </div>

      <Panel title="Derniers arrêts" description="Le détail par camion et par trajet.">
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {recent.map((event) => (
            <ListRow
              key={event.id}
              title={`${texteSur(event.location_name, 'Poste non identifié')}${
                event.country ? ` · ${event.country}` : ''
              }`}
              meta={[
                lisible(event.truck_label) || 'Camion inconnu',
                lisible(event.trip_label),
                lisible(event.driver_label),
                dateTime(event.occurred_at),
              ]
                .filter(Boolean)
                .join(' · ')}
              right={
                <>
                  {event.blockage_reason && (
                    <Badge className="bg-destructive/15 text-destructive">
                      {reasonLabel(event.blockage_reason)}
                    </Badge>
                  )}
                  {event.detection === 'geofence' && (
                    <Badge className="bg-muted text-muted-foreground">
                      <MapPin className="mr-1 h-3 w-3" aria-hidden="true" />
                      Zone suivie
                    </Badge>
                  )}
                  {event.detection === 'status' && (
                    <Badge className="bg-muted text-muted-foreground">Étape du trajet</Badge>
                  )}
                  <span className="readout text-base text-foreground">
                    {duration(event.waited_seconds)}
                  </span>
                </>
              }
            />
          ))}
        </div>
        <BarrePagination pagination={pagination} nom="arrêts" className="mt-0" />
      </Panel>
    </>
  );
}

/** « depuis 2 h 10 », « depuis 2 j 3 h » : jamais une date brute, jamais une durée inventée. */
const depuis = (seconds) => {
  if (seconds === null || seconds === undefined) return 'Date du changement inconnue';
  const total = Math.floor(Math.max(0, seconds) / 60);
  if (total < 1) return 'depuis moins d’une minute';
  if (total >= 24 * 60) {
    const jours = Math.floor(total / (24 * 60));
    const heures = Math.floor((total % (24 * 60)) / 60);
    return `depuis ${jours} j ${heures} h`;
  }
  return `depuis ${duration(seconds)}`;
};

const ETAPE_DOUANE = { EXIT: 'Douane de sortie', ENTRY: 'Douane d’entrée' };

/** Un camion dont le trajet est à une douane : qui, où, depuis quand, et le lien vers le trajet. */
function CamionEnDouane({ camion, maintenant }) {
  const since = camion.since ? new Date(camion.since).getTime() : null;
  const ecoule =
    since !== null && !Number.isNaN(since) ? Math.max(0, (maintenant - since) / 1000) : null;
  const convoi = lisible(camion.convoy_label);
  return (
    <ListRow
      title={lisible(camion.truck_label) || 'Camion inconnu'}
      meta={[
        lisible(camion.driver_label),
        lisible(camion.trip_label),
        convoi ? `Convoi ${convoi}` : null,
        ecoule !== null ? `Étape validée le ${dateTime(camion.since)}` : null,
      ]
        .filter(Boolean)
        .join(' · ')}
      right={
        <>
          <Badge className="bg-muted text-muted-foreground">
            <Fence className="mr-1 h-3 w-3" aria-hidden="true" />
            {ETAPE_DOUANE[camion.side] || 'Douane'}
          </Badge>
          <span className="readout text-base text-foreground">{depuis(ecoule)}</span>
          <Button asChild variant="outline" size="sm">
            <Link to={`/trips/${camion.trip_id}`}>
              Voir le trajet
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
        </>
      }
    />
  );
}

/** Un poste qui a bloqué quelqu'un affiche son motif, pas seulement une couleur. */
function PostRow({ post }) {
  const blocked = num(post.blocked_stops) > 0;
  return (
    <div
      className={
        blocked
          ? 'flex flex-col gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-4 md:flex-row md:items-center md:justify-between'
          : 'flex flex-col gap-3 rounded-xl border border-border bg-muted/40 p-4 md:flex-row md:items-center md:justify-between'
      }
    >
      <div className="min-w-0">
        <p className="font-black text-foreground">
          {texteSur(post.location_name, 'Poste non identifié')}
          {post.country ? (
            <span className="ml-2 text-sm font-medium text-muted-foreground">
              {post.country}
            </span>
          ) : null}
        </p>
        {blocked ? (
          <p className="text-sm font-semibold text-destructive">
            {post.blocked_stops} arrêt(s) bloqué(s) — {reasonLabel(post.top_reason)}
          </p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          {post.stops} passage(s) · médiane {duration(post.median_seconds)} · pire{' '}
          {duration(post.worst_seconds)}
        </p>
      </div>
      <div className="shrink-0 text-left md:text-right">
        <p className="kpi-value text-foreground">{duration(post.total_seconds)}</p>
        <p className="text-xs text-muted-foreground">cumulé</p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Incidents                                                           */
/* ------------------------------------------------------------------ */

function IncidentsView({ items, openCount, totalCount, pagination, openOnly, onToggle }) {
  if (!totalCount) {
    return (
      <CarteVide
        illustration="alerte"
        icon={ShieldAlert}
        title="Aucun incident signalé sur la période"
        lines={[
          'Le bouton d’urgence de l’application chauffeur écrit ici, même si l’entretien n’est pas activé pour votre société.',
          'Une panne signalée par un chauffeur apparaît aussi dans « Entretien et pannes » quand cette rubrique est activée.',
        ]}
        conseil="Un incident remonté depuis le camion arrive ici en quelques secondes, sans que personne au bureau ait à le saisir."
      />
    );
  }

  return (
    <Panel
      title={openOnly ? 'Incidents non résolus' : 'Tous les incidents'}
      description="Gravité, trajet et camion concernés, et lieu au moment de l’alerte."
    >
      <div className="flex items-center justify-between gap-3">
        {openCount ? (
          <p className="text-sm font-semibold text-destructive">
            {openCount} incident(s) encore ouvert(s) sur {totalCount}.
          </p>
        ) : (
          <Reassurance text="Tout ce qui a été signalé sur la période est clôturé." />
        )}
        <Button type="button" variant="outline" size="sm" onClick={onToggle}>
          {openOnly ? 'Voir tout' : 'Ouverts seulement'}
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {items.map((incident) => {
          const critical = (incident.severity || '').toUpperCase() === 'CRITICAL';
          const open = !['closed', 'resolved'].includes(incident.status);
          return (
            <div
              key={incident.id}
              className={`flex flex-col gap-3 border-b border-border/60 p-4 last:border-b-0 md:flex-row md:items-start md:justify-between ${
                open && critical ? 'bg-destructive/10' : ''
              }`}
            >
              <div className="min-w-0 space-y-1">
                <p className="font-black text-foreground">
                  {libelle('incidentType', incident.incident_type, 'Incident')}
                </p>
                {incident.note && (
                  <p className="text-sm text-foreground">{texteSur(incident.note, '')}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {[
                    lisible(incident.truck_label) || 'Camion inconnu',
                    lisible(incident.trip_label),
                    lisible(incident.driver_label),
                    dateTime(incident.occurred_at),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {incident.latitude != null && incident.longitude != null && (
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" aria-hidden="true" />
                    {Number(incident.latitude).toFixed(4)},{' '}
                    {Number(incident.longitude).toFixed(4)}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <Badge
                  className={
                    open && critical
                      ? 'bg-destructive/15 text-destructive'
                      : open
                        ? 'bg-flame/15 text-flame-ink'
                        : 'bg-muted text-muted-foreground'
                  }
                >
                  {libelle('alerteSeverity', incident.severity, 'Gravité non précisée')}
                </Badge>
                {!open && (
                  <Badge className="bg-success/15 text-success">Clôturé</Badge>
                )}
                {incident.relay_status && (
                  <Badge
                    className={
                      incident.relay_status === 'relay_failed'
                        ? 'bg-destructive/15 text-destructive'
                        : 'bg-muted text-muted-foreground'
                    }
                  >
                    {RELAY_LABELS[incident.relay_status] || 'Suivi à vérifier'}
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <BarrePagination pagination={pagination} nom="incidents" className="mt-0" />
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* Départs                                                             */
/* ------------------------------------------------------------------ */

function DeparturesView({ items, total, pagination, failed, failedTotal, paginationFailed, aggregate }) {
  if (!total) {
    return (
      <CarteVide
        illustration="mission"
        icon={ClipboardCheck}
        title="Aucun contrôle avant départ enregistré"
        lines={[
          'Les huit points critiques sont cochés par le chauffeur au moment du départ, depuis l’application.',
          'Tant que le contrôle avant départ n’est pas rendu obligatoire pour votre société, un camion peut partir sans contrôle et rien n’est écrit ici.',
        ]}
        conseil="Le contrôle avant départ est la pièce qu’un client pétrolier réclame en premier après un incident."
      />
    );
  }

  const missing = aggregate.missing_items || [];

  return (
    <div className="space-y-4">
      <Panel
        title="Départs non conformes"
        description="Un départ manquant un point critique reste un départ effectué : c’est ce qui se plaide devant un donneur d’ordre."
      >
        {failed.length ? (
          failed.map((entry) => (
            <div
              key={entry.id}
              className="flex flex-col gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-4 md:flex-row md:items-start md:justify-between"
            >
              <div className="min-w-0 space-y-1">
                <p className="font-black text-foreground">
                  {lisible(entry.truck_label) || 'Camion inconnu'}
                  {lisible(entry.trip_label) ? (
                    <span className="ml-2 text-sm font-medium text-muted-foreground">
                      {lisible(entry.trip_label)}
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-muted-foreground">
                  {[lisible(entry.driver_label), dateTime(entry.completed_at)]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {entry.note && <p className="text-sm text-foreground">{texteSur(entry.note, '')}</p>}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2 md:max-w-xs md:justify-end">
                {(entry.missing_critical || []).map((key) => (
                  <Badge key={key} className="bg-destructive/15 text-destructive">
                    {libelle('checklist', key, 'Point de contrôle')}
                  </Badge>
                ))}
              </div>
            </div>
          ))
        ) : (
          <Reassurance text="Tous les départs de la période ont les huit points critiques cochés." />
        )}
        {failedTotal > 0 ? <BarrePagination pagination={paginationFailed} nom="départs non conformes" className="mt-0" /> : null}
      </Panel>

      {missing.length > 0 && (
        <Panel
          title="Points les plus souvent omis"
          description="Là où le contrôle de départ est le plus souvent oublié."
        >
          <div className="flex flex-wrap gap-2">
            {missing.map((item) => (
              <Badge key={item.key} className="bg-flame/15 text-flame-ink">
                {libelle('checklist', item.key, 'Point de contrôle')} · {item.count}
              </Badge>
            ))}
          </div>
        </Panel>
      )}

      <Panel title="Historique des contrôles" description="Le journal, du plus récent au plus ancien.">
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {items.map((entry) => (
            <ListRow
              key={entry.id}
              title={lisible(entry.truck_label) || lisible(entry.trip_label) || 'Départ'}
              meta={[lisible(entry.driver_label), dateTime(entry.completed_at)]
                .filter(Boolean)
                .join(' · ')}
              right={
                entry.all_critical_checked ? (
                  <Badge className="bg-success/15 text-success">Complet</Badge>
                ) : (
                  <Badge className="bg-destructive/15 text-destructive">
                    {(entry.missing_critical || []).length} point(s) manquant(s)
                  </Badge>
                )
              }
            />
          ))}
        </div>
        <BarrePagination pagination={pagination} nom="contrôles" className="mt-0" />
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Jauges                                                              */
/* ------------------------------------------------------------------ */

function GaugesView({ items, total, pagination, withoutPhoto, withoutPhotoTotal, paginationWithoutPhoto, stats }) {
  if (!total) {
    return (
      <CarteVide
        illustration="donnee"
        icon={Gauge}
        title="Aucun relevé de jauge sur la période"
        lines={[
          'Le chauffeur relève la jauge au chargement puis à la livraison, photo à l’appui.',
          'Dès que les deux côtés existent pour un trajet, l’écart est contrôlé dans « Livraisons à contrôler » selon les tolérances de votre société.',
        ]}
        conseil="Un volume sans photo ne se défend pas : la pièce jointe est saisie par l’application au moment du relevé."
      />
    );
  }

  return (
    <div className="space-y-4">
      {withoutPhotoTotal > 0 && (
        <Panel
          title="Relevés sans photo"
          description="Un volume sans pièce jointe ne se défend pas en cas de contestation."
        >
          <div className="flex flex-wrap gap-2">
            {withoutPhoto.map((item) => (
              <Badge key={item.id} className="bg-flame/15 text-flame-ink">
                {lisible(item.truck_label) || 'Camion inconnu'} ·{' '}
                {libelle('gaugePhase', item.phase, 'Relevé')} · {liters(item.liters)}
              </Badge>
            ))}
          </div>
          <BarrePagination pagination={paginationWithoutPhoto} nom="relevés sans photo" className="mt-0" />
        </Panel>
      )}

      <Panel
        title="Relevés"
        resume={`${liters(stats.loaded_liters)} chargés et ${liters(
          stats.delivered_liters,
        )} livrés sur ${num(stats.trips)} trajet(s).`}
      >
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {items.map((item) => (
            <ListRow
              key={item.id}
              title={`${libelle('gaugePhase', item.phase, 'Relevé')} · ${liters(item.liters)}`}
              meta={[
                lisible(item.truck_label) || 'Camion inconnu',
                lisible(item.trip_label),
                item.compartment ? `Compartiment ${item.compartment}` : null,
                dateTime(item.recorded_at),
              ]
                .filter(Boolean)
                .join(' · ')}
              right={
                item.document_id ? (
                  <Badge className="bg-muted text-muted-foreground">
                    <Camera className="mr-1 h-3 w-3" aria-hidden="true" />
                    Photo
                  </Badge>
                ) : (
                  <Badge className="bg-flame/15 text-flame-ink">Sans photo</Badge>
                )
              }
            />
          ))}
        </div>
        <BarrePagination pagination={pagination} nom="relevés" className="mt-0" />
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pièces de présentation locales                                      */
/* ------------------------------------------------------------------ */

function Panel({ title, description, resume, children }) {
  return (
    <Card className="border-border bg-card shadow-sm">
      <CardHeader>
        <div className="flex items-center gap-1">
          <CardTitle className="text-lg font-black text-foreground">{title}</CardTitle>
          {description && <Aide titre={title}>{description}</Aide>}
        </div>
        {resume && <p className="text-sm text-muted-foreground">{resume}</p>}
      </CardHeader>
      <CardContent className="space-y-2">{children}</CardContent>
    </Card>
  );
}

function ListRow({ title, meta, right }) {
  return (
    <div className="flex flex-col gap-3 border-b border-border/60 p-4 last:border-b-0 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0">
        <p className="font-bold text-foreground">{title}</p>
        <p className="text-sm text-muted-foreground">{meta}</p>
      </div>
      {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
    </div>
  );
}

/** Ce qui va bien tient en une ligne ; cela ne doit pas ressembler à une alerte. */
function Reassurance({ text }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
      <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
      {text}
    </div>
  );
}

/**
 * La grande carte d'état vide.
 *
 * Quatre zéros verts affirmant que tout va bien est exactement la mauvaise
 * réponse quand la vérité est « le terminal n'a jamais rien envoyé ». La carte
 * dit donc trois choses, dans cet ordre : ce qui alimente l'écran, ce qu'il
 * faut vérifier si des camions roulent quand même, et le seul geste utile
 * depuis ici.
 */
function CarteVide({ illustration, icon, title, lines, conseil }) {
  const navigate = useNavigate();
  const etat = useContext(ContexteFraicheur) || {};
  const { derniereMaj, maintenant, loading, enEchec, onRafraichir } = etat;

  return (
    <section className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
      <EmptyState
        illustration={illustration}
        icon={icon}
        title={title}
        aide={
          <>
            {lines.map((line) => (
              <p key={line} className="mt-1 first:mt-0">
                {line}
              </p>
            ))}
            <p className="mt-1">{conseil}</p>
            {/* Pas de lien vers une documentation en ligne: il n'en existe
                aucune, et pointer vers une URL inventée est pire que de ne
                rien proposer. Le parcours d'intégration, lui, existe et
                explique la chaîne. */}
            <Button
              variant="link"
              size="sm"
              className="mt-1 h-auto px-0 py-1"
              onClick={() => navigate(ONBOARDING_ROUTE)}
            >
              Comment ça marche
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </>
        }
        className="border-0 bg-transparent px-6 py-10"
        action={
          // Rien n'arrive ici depuis l'écran lui-même : tout est écrit par
          // l'application du chauffeur. Le seul geste utile est donc d'aller
          // vérifier que les chauffeurs existent et ont reçu leur code d'accès.
          <Button size="sm" onClick={() => navigate('/drivers')}>
            <Users className="h-4 w-4" aria-hidden="true" />
            Vérifier les chauffeurs
          </Button>
        }
      />

      <div className="flex flex-col gap-3 border-t border-border/70 px-4 py-3 text-xs text-muted-foreground md:flex-row md:items-center md:justify-between md:px-6">
        <p>E-CARBU Transport · Des données qui font avancer l’Afrique</p>

        <div className="flex flex-wrap items-center gap-3">
          {/* La pastille ne peut pas rester verte quand le chargement a échoué :
              elle affirmerait « tout va bien » au moment précis où l'écran ne
              sait rien. La couleur suit l'état réel de la liaison. */}
          <span className="inline-flex items-center gap-1.5 font-semibold">
            <span
              className={cn(
                'h-2 w-2 rounded-full',
                enEchec ? 'bg-destructive' : 'bg-success',
              )}
              aria-hidden="true"
            />
            <span className={enEchec ? 'text-destructive' : 'text-success'}>
              {enEchec ? 'Données non chargées' : 'Tous les systèmes opérationnels'}
            </span>
          </span>

          <span className="tabular-nums">{fraicheur(derniereMaj, maintenant)}</span>

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onRafraichir}
            disabled={loading}
            aria-label="Rafraîchir les données terrain"
          >
            <RefreshCw
              className={cn('h-4 w-4', loading && 'animate-spin')}
              aria-hidden="true"
            />
          </Button>
        </div>
      </div>
    </section>
  );
}
